import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { CheckToken } from "../login/AccessToken";
import { C } from "../ipd-billing/constants";
import { formatDateTime } from "../lab-technician/labOrdersShared";
import { getVisitCondition } from "../emergency/api/emergencyApi";
import { TRIAGE } from "../emergency/constants";

function Section({ title, children }) {
  return (
    <Box
      sx={{
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        p: 1.75,
        background: "#fff",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.text, mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Field({ label, value }) {
  if (value == null || String(value).trim() === "") return null;
  return (
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: 12, color: C.textSub }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, color: C.text, whiteSpace: "pre-wrap" }}>
        {value}
      </Typography>
    </Box>
  );
}

function byLine(name, at) {
  const when = at ? formatDateTime(at) : null;
  return [name, when && when !== "-" ? when : null].filter(Boolean).join(" · ");
}

function vitalsText(v) {
  if (!v) return [];
  return [
    v.bp_systolic != null && v.bp_diastolic != null
      ? ["BP", `${v.bp_systolic}/${v.bp_diastolic} mmHg`]
      : null,
    v.heart_rate != null ? ["Heart rate", `${v.heart_rate} bpm`] : null,
    v.respiratory_rate != null
      ? ["Respiratory rate", `${v.respiratory_rate} /min`]
      : null,
    v.temperature != null ? ["Temperature", `${v.temperature} °C`] : null,
    v.spo2 != null ? ["SpO2", `${v.spo2}%`] : null,
    v.consciousness ? ["Consciousness", v.consciousness] : null,
    v.weight_kg != null ? ["Weight", `${v.weight_kg} kg`] : null,
  ].filter(Boolean);
}

/**
 * Read-only emergency visit snapshot for diagnostic review — GET
 * emergency/visits/<visit_id>/condition/.
 */
export default function EmergencyPatientConditionDialog({
  open,
  onClose,
  visitId,
  patientName,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [condition, setCondition] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const resolved = String(visitId || "").trim();
    if (!resolved) {
      setCondition(null);
      setError("Missing emergency visit id for this patient.");
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setCondition(null);

    CheckToken();
    getVisitCondition(resolved)
      .then((data) => {
        if (!cancelled) setCondition(data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load emergency patient condition:", err);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Could not load patient condition.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, visitId]);

  const c = condition || {};
  const triage = c.triage || null;
  const triageMeta = triage?.category ? TRIAGE[triage.category] : null;
  const vitals = vitalsText(c.latest_vitals);
  const allergies = Array.isArray(c.allergies) ? c.allergies : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // Above the report PDF overlay (z-index ~2001).
      sx={{ zIndex: 2200 }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        Patient condition
        {patientName || c.display_name ? (
          <Typography
            component="span"
            sx={{ display: "block", fontSize: 13, fontWeight: 500, color: C.textSub, mt: 0.5 }}
          >
            {[
              patientName || c.display_name,
              "Emergency",
              c.visit_number,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        ) : null}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2, maxHeight: "70vh" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ fontSize: 13 }}>
            {error}
          </Typography>
        ) : !condition ? (
          <Typography sx={{ fontSize: 13, color: C.textSub }}>
            No condition recorded for this emergency visit yet.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            <Section title="Visit">
              <Stack spacing={1}>
                <Field
                  label="Patient"
                  value={[
                    c.display_name,
                    c.age != null ? `${c.age} Y` : null,
                    c.gender,
                  ]
                    .filter(Boolean)
                    .join(" / ")}
                />
                <Field
                  label="Arrived"
                  value={c.arrived_at ? formatDateTime(c.arrived_at) : null}
                />
                <Field label="Chief complaint" value={c.chief_complaint} />
                <Field label="Provider" value={c.provider_name} />
                <Field label="Bed" value={c.bed_label} />
                <Field label="Status" value={c.status} />
                {c.is_mlc ? <Field label="MLC" value="Yes" /> : null}
                {c.disposition?.outcome ? (
                  <Field
                    label="Disposition"
                    value={byLine(c.disposition.outcome, c.disposition.decided_at)}
                  />
                ) : null}
              </Stack>
            </Section>

            {triage?.category ? (
              <Section title="Triage">
                <Stack spacing={1}>
                  <Box>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: 12,
                        fontWeight: 700,
                        color: triageMeta?.text || C.text,
                        background: triageMeta?.bg || "#F3F4F6",
                        border: `1px solid ${triageMeta?.border || C.border}`,
                      }}
                    >
                      {triageMeta?.label || triage.category}
                    </Box>
                    <Typography component="span" sx={{ fontSize: 12, color: C.textSub, ml: 1 }}>
                      {byLine(triage.triaged_by_name, triage.triaged_at)}
                    </Typography>
                  </Box>
                  <Field label="Note" value={triage.note} />
                </Stack>
              </Section>
            ) : null}

            {c.working_diagnosis || c.plan ? (
              <Section title="Assessment">
                <Stack spacing={1}>
                  <Field label="Working diagnosis" value={c.working_diagnosis} />
                  <Field label="Plan" value={c.plan} />
                  {c.assessed_at ? (
                    <Typography sx={{ fontSize: 12, color: C.textSub }}>
                      {formatDateTime(c.assessed_at)}
                    </Typography>
                  ) : null}
                </Stack>
              </Section>
            ) : null}

            {vitals.length > 0 ? (
              <Section title="Latest vitals">
                <Stack spacing={1}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                      gap: 1,
                    }}
                  >
                    {vitals.map(([label, value]) => (
                      <Field key={label} label={label} value={value} />
                    ))}
                  </Box>
                  <Field label="Notes" value={c.latest_vitals?.notes} />
                  <Typography sx={{ fontSize: 12, color: C.textSub }}>
                    {byLine(
                      c.latest_vitals?.recorded_by_name,
                      c.latest_vitals?.recorded_at,
                    )}
                  </Typography>
                </Stack>
              </Section>
            ) : null}

            {allergies.length > 0 ? (
              <Section title="Allergies">
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {allergies.map((a, idx) => (
                    <li key={a.id || idx}>
                      <Typography component="span" sx={{ fontSize: 13 }}>
                        {[a.allergen, a.reaction, a.severity]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Section>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button onClick={onClose} variant="contained" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
